import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { BACKLOG_BASE } from '../project/index.js';

/** 展開が終わったときの結果。失敗なら `error` が入る。 */
export type ExpandResult =
  | {
      /** 成功。 */
      kind: 'done';
      /** 作った / 残した子票の課題キー。 */
      issueKeys: string[];
      /** 消した課題キー。 */
      removed: string[];
      /** 完了済み / 実績pt ありで残した課題キー。 */
      retained: string[];
      /** 母票へ書いた合計 pt。 */
      totalPoint: number;
    }
  | {
      /** 失敗。 */
      kind: 'failed';
      /** 失敗の理由（API のメッセージ）。 */
      reason: string;
    };

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 結果の内訳 1 行。消した / 残した子票のように 0 件なら出さない項目をまとめて扱う
 * @graph-connects none
 */
function ResultRow({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--color-border)] py-2 last:border-b-0">
      <span className="text-xs text-[var(--color-text-muted)]">{label}</span>
      <span className="text-sm text-[var(--color-text-primary)]">{value}</span>
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計展開が終わったことを知らせるダイアログ。作った子票の枚数と合計 pt、消した / 残した子票の枚数を出し、展開先の Backlog 課題へ飛べるようにする。失敗したときは理由をそのまま見せる
 * @graph-connects none
 */
export function ChildTicketExpandResultDialog({
  result,
  onClose,
}: {
  /** 結果。null なら閉じている。 */
  result: ExpandResult | null;
  /** 閉じる。 */
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  if (result === null) return null;

  const failed = result.kind === 'failed';
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={t(
        failed
          ? 'childTicketPlan.expand.result.failedTitle'
          : 'childTicketPlan.expand.result.title',
      )}
      className="child-expand-dialog"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-tone="neutral"
            className="child-sheet-button"
            onClick={onClose}
          >
            {t('childTicketPlan.expand.result.close')}
          </button>
        </div>
      }
    >
      {result.kind === 'failed' ? (
        <div className="space-y-3">
          <p className="text-sm leading-7 text-[var(--color-text-secondary)]">
            {t('childTicketPlan.expand.result.failedBody')}
          </p>
          <p
            className="rounded-lg px-3 py-2 font-mono text-xs leading-6 break-all"
            style={{
              background: 'color-mix(in srgb, var(--color-error) 12%, transparent)',
              color: 'var(--color-error)',
            }}
          >
            {result.reason}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm leading-7 text-[var(--color-text-secondary)]">
            {t('childTicketPlan.expand.result.body')}
          </p>
          <div>
            <ResultRow
              label={t('childTicketPlan.history.children')}
              value={t('childTicketPlan.history.sheets', { count: result.issueKeys.length })}
            />
            <ResultRow
              label={t('childTicketPlan.history.totalPoint')}
              value={<span className="font-mono tabular-nums">{result.totalPoint}</span>}
            />
            {result.removed.length === 0 ? null : (
              <ResultRow
                label={t('childTicketPlan.history.removed')}
                value={t('childTicketPlan.history.sheets', { count: result.removed.length })}
              />
            )}
            {/* 残した子票は「消したかったのに消せなかった」ぶん。黙って消えないことを見せる */}
            {result.retained.length === 0 ? null : (
              <ResultRow
                label={t('childTicketPlan.history.retained')}
                value={t('childTicketPlan.history.sheets', { count: result.retained.length })}
              />
            )}
          </div>
          {result.issueKeys.length === 0 ? null : (
            <div className="flex flex-wrap gap-1.5">
              {result.issueKeys.map((issueKey) => (
                <a
                  key={issueKey}
                  href={`${BACKLOG_BASE}${issueKey}`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-full bg-[var(--color-bg-elevated)] px-2.5 py-1 font-mono text-[11px] text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-hover)]"
                >
                  {issueKey}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
